/**
 * useOrgTree Hook
 *
 * **역할:**
 * - 조직(OrgUnit)과 직원(Employee) 데이터를 트리 구조로 가공
 * - 각 조직의 인원 수 계산 (직접 소속 + 하위 조직 전체)
 * - 조직별 구성원 목록 제공 (조직장 → 직위 순 → 입사일 순)
 * - Chapter 4-3: 조직도(Org Tree) 기능
 *
 * **V104 개선사항:**
 * - 비활성 조직 숨김 정책 강화 (status !== 'active' 철저 필터링)
 * - 하드코딩된 직위 순서 → positionLevels.rankWeight 기반 정렬
 * - orgManagerEmployeeId 우선 + leaderId fallback 조직장 판정
 * - 근속연수: regularHireDate 우선, ngleFirstHireDate fallback
 *
 * **V137.5 변경:**
 * - 직원 조회 API를 getOrgChartEmployees로 변경 (HR/Admin 권한 불필요)
 * - 일반 사용자도 조직도 조회 가능
 *
 * **사용 예시:**
 * ```tsx
 * function OrgTreePage() {
 *   const { treeData, getOrgMembers, isLoading, error } = useOrgTree();
 *
 *   const members = getOrgMembers('team_a'); // A팀 구성원 조회
 *
 *   return (
 *     <div>
 *       {treeData.map(node => (
 *         <OrgTreeNode
 *           key={node.orgUnit.id}
 *           node={node}
 *           onSelect={(id) => setSelectedOrg(id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useMemo } from 'react';
import { useOrgUnitsPublic } from './useOrgUnitsPublic';
import { useOrgChartEmployees, OrgChartEmployee } from './useOrgChartEmployees';
import { usePositionLevels } from './usePositionLevels';
import { useJobTitles } from './useJobTitles';
import { OrgUnit } from '../types/orgUnit';

/**
 * 조직 트리 노드
 *
 * 계층 구조를 표현하는 트리 노드입니다.
 */
export interface OrgTreeNode {
  /**
   * 조직 정보
   */
  orgUnit: OrgUnit;

  /**
   * 하위 조직 노드 목록
   */
  children: OrgTreeNode[];

  /**
   * 직접 소속 인원 수
   *
   * 이 조직에 직접 소속된 직원 수 (하위 조직 제외)
   */
  memberCount: number;

  /**
   * 전체 인원 수
   *
   * 직접 소속 인원 + 하위 조직 전체 인원의 합
   */
  totalMemberCount: number;

  /**
   * 조직장 정보
   *
   * leaderId로 조회한 조직장의 OrgChartEmployee 정보
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  leader?: OrgChartEmployee;

  /**
   * 겸임 조직장 여부
   *
   * - true: 조직장의 주 소속 조직(orgUnitId)이 현재 조직과 다름 (겸임)
   * - false or undefined: 주 소속 조직과 일치
   */
  isConcurrentLeader?: boolean;
}

/**
 * useOrgTree Hook 반환 타입
 */
export interface UseOrgTreeResult {
  /**
   * 조직 트리 데이터
   *
   * 최상위 조직(parentId=null)부터 시작하는 트리 구조
   */
  treeData: OrgTreeNode[];

  /**
   * 로딩 중 여부
   */
  isLoading: boolean;

  /**
   * 에러 정보
   */
  error: Error | null;

  /**
   * 조직별 구성원 조회 함수
   *
   * @param orgUnitId 조직 ID
   * @returns 구성원 목록 (조직장 → 직위 순 → 입사일 순)
   *
   * **정렬 규칙:**
   * 1. 조직장이 최상단
   * 2. 조직장은 jobTitle(실장, 팀장 등)로 표시
   * 3. 일반 구성원은 positionTitle(차장, 과장 등 직위)로 표시
   * 4. 정렬: 조직장 → 직위 높은 순 → 직위 같으면 입사일 빠른 순
   *
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  getOrgMembers: (orgUnitId: string) => OrgChartEmployee[];
}

/**
 * V29.15: useOrgTree 필터 옵션
 */
export interface UseOrgTreeOptions {
  requestedCompanyCode?: 'KR' | 'CN' | 'ALL';
}

/**
 * useOrgTree Hook
 *
 * V29.15: requestedCompanyCode 옵션 추가
 * - system_admin/role_global만 국가 선택 가능
 * - 그 외 사용자는 자동으로 본인 스코프 적용
 */
export function useOrgTree(options?: UseOrgTreeOptions): UseOrgTreeResult {
  // ============================================
  // 데이터 조회
  // ============================================

  // V40: 조직 목록 조회 (일반 사용자용 Firestore 직접 조회)
  // - useOrgUnits(관리자용 CF) → useOrgUnitsPublic(Firestore 직접) 변경
  // - 모든 로그인 사용자가 조직도 페이지 조회 가능
  const {
    orgUnits,
    isLoading: isLoadingOrgUnits,
    error: orgUnitsError,
  } = useOrgUnitsPublic({
    isActive: true,
    companyCode: options?.requestedCompanyCode === 'ALL' ? undefined : options?.requestedCompanyCode,
  });

  // V137.5: 조직도 전용 직원 목록 조회 (HR/Admin 권한 불필요)
  const {
    employees,
    isLoading: isLoadingEmployees,
    error: employeesError,
  } = useOrgChartEmployees();

  // V104: 직위 마스터 조회 (rankWeight 기반 정렬용)
  const {
    positionLevels,
    isLoading: isLoadingPositionLevels,
  } = usePositionLevels({ status: 'active' });

  // V133: 직책 마스터 조회 (order 기반 정렬용)
  const {
    jobTitles,
    isLoading: isLoadingJobTitles,
  } = useJobTitles({ status: 'active' });

  // ============================================
  // 상태 관리
  // ============================================

  const [treeData, setTreeData] = useState<OrgTreeNode[]>([]);
  const isLoading = isLoadingOrgUnits || isLoadingEmployees || isLoadingPositionLevels || isLoadingJobTitles;
  const error = orgUnitsError || employeesError;

  // ============================================
  // V104: 직위별 rankWeight 맵 (메모이제이션)
  // ============================================
  const positionRankMap = useMemo(() => {
    const map = new Map<string, number>();
    positionLevels.forEach((pl) => {
      // 직위명(name)을 키로 rankWeight 저장
      if (pl.name) {
        map.set(pl.name, pl.rankWeight ?? 0);
      }
    });
    return map;
  }, [positionLevels]);

  // ============================================
  // V133: 직책별 order 맵 (메모이제이션)
  // ============================================
  const jobTitleOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    jobTitles.forEach((jt) => {
      // 직책명(name)을 키로 order 저장
      if (jt.name) {
        map.set(jt.name, jt.order ?? 999);
      }
    });
    return map;
  }, [jobTitles]);

  // ============================================
  // 헬퍼 함수
  // ============================================

  /**
   * V104: 조직장 ID 조회 (orgManagerEmployeeId 우선, leaderId fallback)
   */
  const getOrgLeaderId = (orgUnit: OrgUnit): string | null => {
    return orgUnit.orgManagerEmployeeId || orgUnit.leaderId || null;
  };

  /**
   * V133: 직책(jobTitle) 순서 가중치 (order 기반)
   *
   * order가 작을수록 높은 직책 (대표=1, 본부장=2, ... 팀원=6)
   */
  const getJobTitleWeight = (jobTitle?: string): number => {
    if (!jobTitle) return 999; // 직책 없으면 가장 뒤로
    const order = jobTitleOrderMap.get(jobTitle);
    if (order === undefined) return 999; // 매핑 안 되면 가장 뒤로
    return order;
  };

  /**
   * V104: 직위(positionTitle) 순서 가중치 (rankWeight 기반)
   *
   * rankWeight가 클수록 높은 직위 → 정렬 시 내림차순 (높은 직위 먼저)
   * 정렬용으로 -rankWeight 반환 (작은 값이 먼저 오도록)
   */
  const getPositionWeight = (positionTitle?: string): number => {
    if (!positionTitle) return 999; // 직위 없으면 가장 뒤로
    const rankWeight = positionRankMap.get(positionTitle);
    if (rankWeight === undefined) return 999; // 매핑 안 되면 가장 뒤로
    // rankWeight가 큰 직위가 먼저 오도록 음수로 변환
    return -rankWeight;
  };

  /**
   * V104: 입사일 조회 (firstHireDate 우선, ngleFirstHireDate fallback)
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  const getHireDateForSort = (emp: OrgChartEmployee): string => {
    return emp.firstHireDate || emp.ngleFirstHireDate || '9999-12-31';
  };

  /**
   * 조직별 구성원 조회 및 정렬
   *
   * @param orgUnitId 조직 ID
   * @returns 정렬된 구성원 목록
   *
   * **V133 정렬 정책:**
   * 1. 직책순 (jobTitle order 기준, 대표=1 > 본부장=2 > ... > 팀원=6)
   * 2. 직위순 (positionTitle rankWeight 기준, 높을수록 상위)
   * 3. 입사일순 (firstHireDate 빠른 사람이 위)
   * 4. 이름순 (동일 조건 시)
   *
   * **표기 정책 (OrgChartPage에서 적용):**
   * - 조직 운영 역할자(order <= 5): jobTitle (실장, 팀장 등) 표시
   * - 일반 구성원(팀원): positionTitle (차장, 과장 등) 표시
   *
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  const getOrgMembers = (orgUnitId: string): OrgChartEmployee[] => {
    // 해당 조직의 구성원 필터링
    const members = employees.filter((emp) => emp.orgUnitId === orgUnitId);

    // 정렬
    const sortedMembers = [...members].sort((a, b) => {
      // 1. 직책순 (order가 작을수록 높은 직책)
      const aJobTitleWeight = getJobTitleWeight(a.jobTitle);
      const bJobTitleWeight = getJobTitleWeight(b.jobTitle);

      if (aJobTitleWeight !== bJobTitleWeight) {
        return aJobTitleWeight - bJobTitleWeight;
      }

      // 2. 직위순 (rankWeight가 클수록 높은 직위)
      const aPositionWeight = getPositionWeight(a.positionTitle);
      const bPositionWeight = getPositionWeight(b.positionTitle);

      if (aPositionWeight !== bPositionWeight) {
        return aPositionWeight - bPositionWeight;
      }

      // 3. 입사일순 (빠른 순)
      const aDate = getHireDateForSort(a);
      const bDate = getHireDateForSort(b);
      if (aDate !== bDate) {
        return aDate.localeCompare(bDate);
      }

      // 4. 입사일도 같으면 이름순
      return (a.nameKo || '').localeCompare(b.nameKo || '');
    });

    return sortedMembers;
  };

  /**
   * 조직의 직접 소속 인원 수 계산
   */
  const getMemberCount = (orgUnitId: string): number => {
    return employees.filter((emp) => emp.orgUnitId === orgUnitId).length;
  };

  /**
   * V104: 조직장 정보 조회
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  const getLeader = (orgUnit: OrgUnit): OrgChartEmployee | undefined => {
    const leaderId = getOrgLeaderId(orgUnit);
    if (!leaderId) return undefined;
    return employees.find((emp) => emp.id === leaderId);
  };

  /**
   * 겸임 조직장 여부 확인
   *
   * 조직장의 주 소속 조직(orgUnitId)이 현재 조직과 다른지 확인
   * V137.5: Employee → OrgChartEmployee 타입 변경
   */
  const isConcurrentLeader = (leader: OrgChartEmployee | undefined, orgUnitId: string): boolean => {
    if (!leader) return false;
    return leader.orgUnitId !== orgUnitId;
  };

  /**
   * 하위 조직 전체의 인원 수 계산 (재귀)
   *
   * @param node 조직 트리 노드
   * @returns 직접 소속 + 하위 조직 전체 인원 수
   */
  const calculateTotalMemberCount = (node: OrgTreeNode): number => {
    let total = node.memberCount;

    for (const child of node.children) {
      total += calculateTotalMemberCount(child);
    }

    return total;
  };

  /**
   * 조직 트리 구조 생성 (재귀)
   *
   * @param parentId 상위 조직 ID (null이면 최상위)
   * @returns 트리 노드 목록
   *
   * **비활성 조직 처리 정책:**
   * - status !== 'active' 조직은 트리에서 제외
   * - 고아 조직(부모가 비활성인 active 조직)도 트리에서 제외 (숨김 처리)
   * - useOrgUnits({ isActive: true })로 이미 필터링되지만, 방어적으로 재확인
   */
  const buildTree = (parentId: string | null): OrgTreeNode[] => {
    // parentId가 같은 조직들 필터링
    const children = orgUnits.filter((org) => org.parentId === parentId);

    // ⭐ 방어적 프로그래밍: status='active' 명시적 재확인
    const activeChildren = children.filter((org) => org.status === 'active');

    // 비활성 조직이 섞여있을 경우 경고 로그 (이론적으로는 발생하지 않아야 함)
    if (children.length > activeChildren.length) {
    }

    // order 필드로 정렬
    const sortedChildren = [...activeChildren].sort((a, b) => (a.order || 0) - (b.order || 0));

    // 각 조직을 트리 노드로 변환
    return sortedChildren.map((orgUnit) => {
      // 기본 노드 생성
      // V104: getLeader에 orgUnit 전달 (orgManagerEmployeeId 우선)
      const node: OrgTreeNode = {
        orgUnit,
        children: buildTree(orgUnit.id), // 재귀: 하위 조직 생성
        memberCount: getMemberCount(orgUnit.id),
        totalMemberCount: 0, // 나중에 계산
        leader: getLeader(orgUnit),
      };

      // 조직장 정보 설정
      if (node.leader) {
        node.isConcurrentLeader = isConcurrentLeader(node.leader, orgUnit.id);
      }

      // 전체 인원 수 계산 (재귀)
      node.totalMemberCount = calculateTotalMemberCount(node);

      return node;
    });
  };

  // ============================================
  // Effect: 트리 데이터 생성 + 고아 조직/비활성 조직 소속 직원 감지
  // ============================================

  useEffect(() => {
    if (!isLoading && !error) {
      const tree = buildTree(null); // 최상위 조직부터 시작
      setTreeData(tree);

      // ============================================
      // ⭐ 고아 조직 감지 (부모가 비활성인 active 조직)
      // ============================================
      const orphanOrgs = orgUnits.filter((org) => {
        // parentId가 null이면 최상위이므로 고아가 아님
        if (org.parentId === null) return false;

        // parentId가 있는데, orgUnits에 부모가 없으면 고아
        const parentExists = orgUnits.some((parent) => parent.id === org.parentId);
        return !parentExists;
      });

      if (orphanOrgs.length > 0) {
      }

      // ============================================
      // ⭐ 비활성 조직에 소속된 active 직원 감지
      // ============================================
      const orgUnitIds = new Set(orgUnits.map((org) => org.id));
      const employeesInInactiveOrgs = employees.filter((emp) => {
        // orgUnitId가 없으면 미배정 직원 (조직도 표시 안 됨)
        if (!emp.orgUnitId) return false;

        // orgUnitId가 있는데, active 조직 목록에 없으면 비활성 조직 소속
        return !orgUnitIds.has(emp.orgUnitId);
      });

      if (employeesInInactiveOrgs.length > 0) {
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnits, employees, positionRankMap, isLoading, error]);

  // ============================================
  // 반환
  // ============================================

  return {
    treeData,
    isLoading,
    error,
    getOrgMembers,
  };
}

/**
 * ============================================
 * 향후 확장 계획
 * ============================================
 *
 * **1. 조직 검색**
 * - 조직명으로 검색
 * - 검색 결과 하이라이트
 *
 * **2. 조직 접기/펼치기**
 * - 트리 노드 expand/collapse 상태 관리
 * - 로컬스토리지에 상태 저장
 *
 * **3. 조직 이동 (Drag & Drop)**
 * - 조직을 드래그하여 다른 상위 조직으로 이동
 * - parentId, path 자동 업데이트
 *
 * **4. 인원 수 필터링**
 * - 특정 인원 수 이상/이하 조직만 표시
 * - 빈 조직 숨기기
 *
 * **5. 조직도 내보내기**
 * - PNG/PDF로 조직도 다운로드
 * - Excel로 조직 목록 다운로드
 */
